/**
 * Wake the Function before the person needs it.
 *
 * Measured against the live API on 2026-07-28: the first synthesis after an idle period took
 * **2.09 s**, and the three that followed took 0.44–0.62 s. That ~1.5 s is a cold Azure Functions
 * host, and it lands on the first sentence of the day — the one most likely to be "guten Morgen"
 * to someone standing in the doorway, and the one where a person is most likely to conclude the
 * device is broken.
 *
 * The ping costs nothing: `GET /api/tts` without a valid sample is rejected by our own handler
 * before it ever calls Azure Speech (404 with `ALLOW_SAMPLE_GET` unset, as production must be),
 * measured at 113–175 ms warm. So the host loads our code and no synthesis is billed.
 *
 * Deliberately fire-and-forget and deliberately silent on failure — this is an optimisation, and an
 * optimisation must never be able to delay or break startup.
 *
 * ── Why this expires (C82, `SHL-10`) ──
 *
 * It used to be a `let warmed = false` latched for the lifetime of the page. That is the right shape for
 * a page that opens, is used and closes, and the wrong shape for **this** device: it is left alone for
 * hours and woken, and the host goes cold over exactly the period that also suspends the audio context.
 * So the one moment the warm-up is most needed is the one moment a once-per-page latch guarantees it
 * will not happen.
 */

/** Long enough that ordinary use never re-pings, short enough that a nap always does. */
const WARM_TTL_MS = 5 * 60 * 1000;

const nowMs = (): number => (typeof performance === 'undefined' ? 0 : performance.now());

let warmedAtMs: number | null = null;
/** One request at a time: `visibilitychange` and `pageshow` can both fire on the same return. */
let inFlight: Promise<void> | null = null;

export async function warmUpTts(endpoint = '/api/tts', { force = false } = {}): Promise<void> {
  if (inFlight) return inFlight;
  if (!force && warmedAtMs !== null && nowMs() - warmedAtMs < WARM_TTL_MS) return;
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;

  const startedAt = nowMs();
  warmedAtMs = startedAt;

  inFlight = (async () => {
    try {
      // No body, no voice, no text: nothing here can produce audio or reach Speech.
      await fetch(`${endpoint}?warm=1`, { method: 'GET', cache: 'no-store' });
      console.info('[tts] warm-up done', `${Math.round(nowMs() - startedAt)}ms`);
    } catch {
      // Offline, or the endpoint is unreachable. Both are handled properly at speak time by the
      // provider chain, which is where the person can actually be told. Clearing the stamp means the
      // next return tries again rather than believing a warm-up that never happened.
      warmedAtMs = null;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

/** When the endpoint was last warmed, for the Diagnose panel. Null if never, or if the last try failed. */
export function warmedAgoMs(): number | null {
  return warmedAtMs === null ? null : Math.round(nowMs() - warmedAtMs);
}

/** Test seam. */
export function resetWarmUpForTests(): void {
  warmedAtMs = null;
  inFlight = null;
}
